import {
  HttpContextToken,
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpStatusCode,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import {
  isThrottledError,
  SessionCoordinatorService,
} from '../services/session-coordinator.service';

const IS_REFRESH_RETRY = new HttpContextToken<boolean>(() => false);

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const authService = inject(AuthService);
  const coordinator = inject(SessionCoordinatorService);

  const authenticatedRequest = request.clone({
    withCredentials: true,
  });

  return next(authenticatedRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      if (!shouldTryRefresh(authenticatedRequest, error)) {
        return throwError(() => error);
      }

      const retriedRequest = authenticatedRequest.clone({
        context: authenticatedRequest.context.set(IS_REFRESH_RETRY, true),
      });

      return from(coordinator.ensureSessionFresh()).pipe(
        catchError((refreshError) => {
          // A throttled refresh (429) is the API asking for less traffic, not a session it refuses
          // — the refresh token is still valid and the coordinator has already backed off. Clearing
          // the user here turns a rate limit into a sign-out, which is what it used to do.
          if (!isThrottledError(refreshError)) {
            authService.clearUser();
          }
          return throwError(() => refreshError);
        }),
        switchMap(() => next(retriedRequest))
      );
    })
  );
};

function shouldTryRefresh(
  request: Parameters<HttpInterceptorFn>[0],
  error: HttpErrorResponse
): boolean {
  if (error.status !== HttpStatusCode.Unauthorized) {
    return false;
  }

  if (request.context.get(IS_REFRESH_RETRY)) {
    return false;
  }

  const url = request.url.toLowerCase();
  if (
    url.includes('/auth/login') ||
    url.includes('/auth/register') ||
    url.includes('/auth/forgot-password') ||
    url.includes('/auth/validate-account') ||
    url.includes('/auth/reset-password') ||
    // The refresh/logout endpoints must never trigger another refresh attempt.
    // A 401 from /auth/refresh means the refresh token itself is invalid/expired
    // (TOKEN_EXPIRED) — retrying would deadlock the single-flight refresh and
    // prevent the failure path (logout + redirect to /login) from ever running.
    url.includes('/auth/refresh') ||
    url.includes('/auth/logout')
  ) {
    return false;
  }

  return true;
}
